import qs from 'qs';
import { CORE_WEB_ORIGIN } from '@reverbdotcom/env';
import { Location } from 'react-router';
import wrapped from './window_wrapper';

interface CoreLinkLocation {
  // The ID of the dom element we will scroll to
  anchor?: string;

  // Query params for the request
  query?: object;

  // Pathname with or without ORIGIN
  pathname?: string;

  // Serialized query string (v7-style, e.g. `?condition[]=used`); used when
  // no `query` object is present. An explicit `query` wins, so spreading a
  // location (which always carries `search`) and overriding `query` behaves
  // as intended.
  search?: string;

  // Serialized hash (e.g. `#reviews`); takes precedence over `anchor`
  hash?: string;
}

export type ToTarget = string | CoreLinkLocation;

export interface AbsoluteLocation extends Location {
  origin?: string;
}

export function isActive(to: Partial<AbsoluteLocation>, current?: Partial<Location>) {
  return (
    current && // We know what page we're on
    (!to.pathname || to.pathname === current.pathname) && // the 'to' has a matching pathname (or is anchor-only)
    to.hash === current.hash
  );
}

/**
 * Converts a `query` object (v3-style, still used throughout this file) into
 * a `search` string, using the same bracket-array format the old
 * `RouterHistory` used (e.g. `?condition[]=used`), so client-side
 * navigations serialize queries identically to full page loads.
 */
export function serializeQuery(query: Record<string, any> | undefined): string {
  if (!query || Object.keys(query).length === 0) {
    return '';
  }

  const queryString = qs.stringify(query, { arrayFormat: 'brackets', encodeValuesOnly: true });
  return queryString ? `?${queryString}` : '';
}

/**
 * Inverse of serializeQuery: parses a `search` string (with or without the
 * leading `?`) into a params object.
 */
export function parseQuery(search: string | undefined): Record<string, any> {
  if (!search) {
    return {};
  }

  return qs.parse(search, { ignoreQueryPrefix: true });
}

export function serializeURL(url: AbsoluteLocation): string {
  if (!url) {
    return '';
  }

  const { origin, pathname, search, hash } = url;
  const href = `${origin || ''}${pathname || ''}${search}${hash || ''}`;

  return href;
}

// Yes this is set to the string, null in production while in RRE.
// FIXME: expose a `currentOrigin` from @reverbdotcom/env that takes into account the current app.
// This assumes it's always going to be core/reverb-ui
const currentOrigin = (wrapped.location.origin && wrapped.location.origin !== 'null') ?
  wrapped.location.origin :
  CORE_WEB_ORIGIN;

export function parseURL(to: ToTarget): AbsoluteLocation {
  if (!to) {
    return {
      hash: '',
      key: 'default',
      pathname: '',
      search: '',
      state: '',
    };
  }

  if (typeof to === 'string') {
    const u = new URL(to, currentOrigin);

    const parsed: AbsoluteLocation = {
      hash: u.hash,
      key: 'default',
      pathname: u.pathname,
      search: u.search,
      state: '',
    };

    if (u.origin !== currentOrigin) {
      parsed.origin = u.origin;
    }

    return parsed;
  }

  let hash = to.hash ?? '';
  if (!hash && to.anchor) {
    hash = `#${to.anchor}`;
  }

  const search = to.query ? serializeQuery(to.query) : (to.search ?? '');

  return {
    hash,
    key: 'default',
    pathname: to.pathname ?? '',
    search,
    state: '',
  };
}

// Converts a ToTarget (possibly a v3-style { pathname, query } descriptor)
// into a target react-router's navigate()/<Link to> understands -- v7
// silently ignores a `query` key, so it must be serialized into `search`.
export function toNavigateTarget(to: ToTarget): string | AbsoluteLocation {
  if (typeof to === 'string') {
    return to;
  }

  return parseURL(to);
}
